import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import { SxProps } from "@mui/system";
import Breadcrumbs from "@mui/joy/Breadcrumbs";
import ReactRouterLink from "../ReactRouterLink/ReactRouterLink";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import LinkButton from "../LinkButton/LinkButton";
import { useNavigate } from "react-router-dom";

interface Props {
  title: string;
  breadCrumbs?: {
    text: string;
    url?: string;
  }[];
  endDecorator?: JSX.Element;
  subTitle?: string;
  sx?: SxProps;
  showGoBack?: boolean;
  // Where Go Back leads when there's no in-app history, e.g. a page opened in a new tab
  goBackFallback?: string;
}

const PageHeader = ({ title, breadCrumbs, showGoBack, goBackFallback, subTitle, endDecorator, sx = [] }: Props) => {
  const navigate = useNavigate();
  return (
    <Stack
      className="page-header"
      flexDirection={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      display={"flex"}
      flexWrap={"wrap"}
      mb={3}
      gap={{
        xs: 2,
        md: 0,
      }}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Stack className="header-and-breadcrumbs">
        {showGoBack && (
          <LinkButton
            size="sm"
            variant="plain"
            // React Router keeps the history index in `state.idx`, it's 0 on the first page of a tab
            to={goBackFallback && !window.history.state?.idx ? goBackFallback : -1}
            sx={{ width: "max-content" }}
            startDecorator={<KeyboardBackspaceOutlinedIcon />}
          >
            Go Back
          </LinkButton>
        )}

        {breadCrumbs && breadCrumbs?.length > 0 && (
          <Breadcrumbs
            aria-label="breadcrumbs"
            separator={<span>/</span>}
            sx={{
              fontSize: "sm",
              textTransform: "capitalize",
              px: 0,
              pb: 0.2,
            }}
          >
            {breadCrumbs?.map((item, index) =>
              item.url ? (
                <ReactRouterLink key={item.text} color="neutral" to={`${item.url}`}>
                  {item.text}
                </ReactRouterLink>
              ) : (
                <Typography
                  key={item.text}
                  level="body-sm"
                  sx={{
                    color: breadCrumbs.length - 1 === index ? "neutral.800" : "neutral",
                    cursor: "default",
                  }}
                >
                  {item.text}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        )}
        {title && (
          <Typography
            level="h2"
            component="h1"
            sx={{
              fontSize: "28px",
            }}
          >
            {title}
          </Typography>
        )}
        {subTitle && (
          <Typography level="body-sm" fontSize={"16px"}>
            {subTitle}
          </Typography>
        )}
      </Stack>

      <Stack
        id="page-header-end-decorator"
        flexDirection={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
        gap={2}
      >
        {endDecorator || null}
      </Stack>
    </Stack>
  );
};
export default PageHeader;
